import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { listOffers, deleteOffer } from "@/lib/api";
import { Plus, Trash, ArrowRight, Sparkle, Scales, X } from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";
import Footer from "@/components/Footer";
import AnalyticsWidget from "@/components/AnalyticsWidget";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [compareMode, setCompareMode] = useState(false);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/login", { replace: true });
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) {
      listOffers()
        .then(setOffers)
        .catch(() => toast.error("Could not load your offers"))
        .finally(() => setFetching(false));
    }
  }, [user]);

  const handleDelete = async (id) => {
    try {
      await deleteOffer(id);
      setOffers((prev) => prev.filter((o) => o.id !== id));
      setSelected((prev) => prev.filter((sid) => sid !== id));
      toast.success("Offer removed");
    } catch {
      toast.error("Could not remove offer");
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((p) => p !== id);
      if (prev.length === 2) return [prev[1], id];
      return [...prev, id];
    });
  };

  const enterCompare = () => {
    setCompareMode(true);
    setSelected([]);
  };
  const exitCompare = () => {
    setCompareMode(false);
    setSelected([]);
  };

  const launchCompare = () => {
    if (selected.length !== 2) return;
    navigate(`/compare?a=${selected[0]}&b=${selected[1]}`);
  };

  const canCompare = useMemo(() => offers.length >= 2, [offers]);

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-2 border-[#C9A961] border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A1628] text-[#F5F1E8] flex flex-col">
      <Navbar />
      <Toaster theme="dark" position="top-right" />

      <div className="flex-1 max-w-7xl mx-auto w-full px-6 md:px-10 py-12 md:py-16">
        <AnalyticsWidget />
        <div className="grid grid-cols-12 gap-8 items-end mb-10">
          <div className="col-span-12 md:col-span-8">
            <p className="text-sm tracking-[0.3em] uppercase font-bold text-[#C9A961] mb-3">
              Your private vault
            </p>
            <h1 className="font-display text-4xl md:text-5xl font-light tracking-tighter leading-none">
              Welcome back,{" "}
              <span className="font-black">{user.name.split(" ")[0]}</span>.
            </h1>
            <p className="mt-4 text-[#9AA5B5] max-w-xl">
              Every Irresistible Offer you forge is saved here. Open, share,
              compare, or export any of them.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 md:text-right flex md:justify-end gap-3 flex-wrap">
            {canCompare && !compareMode && (
              <Button
                onClick={enterCompare}
                variant="outline"
                className="bg-transparent border-white/15 text-[#F5F1E8] hover:bg-white/5 rounded-sm"
                data-testid="dashboard-compare-mode-btn"
              >
                <Scales weight="bold" className="mr-2 h-4 w-4" /> Compare
              </Button>
            )}
            <Button
              onClick={() => navigate("/new")}
              className="bg-[#C9A961] text-[#0A1628] font-bold hover:bg-[#E0B64B] rounded-sm px-6 py-5"
              data-testid="dashboard-new-offer-btn"
            >
              <Plus weight="bold" className="mr-2 h-5 w-5" />
              Forge new offer
            </Button>
          </div>
        </div>

        {compareMode && (
          <div
            className="mb-6 bg-[#0F1F38] border border-[#C9A961]/30 rounded-lg p-5 flex flex-wrap items-center justify-between gap-3"
            data-testid="compare-toolbar"
          >
            <div className="text-sm text-[#F5F1E8]">
              Pick <span className="font-bold text-[#C9A961]">2 offers</span> to
              compare side by side.{" "}
              <span className="text-[#9AA5B5]">Selected: {selected.length}/2</span>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={launchCompare}
                disabled={selected.length !== 2}
                className="bg-[#C9A961] text-[#0A1628] font-bold hover:bg-[#E0B64B] rounded-sm disabled:opacity-40"
                data-testid="compare-launch-btn"
              >
                <Scales weight="bold" className="mr-2 h-4 w-4" />
                Compare selected
              </Button>
              <Button
                onClick={exitCompare}
                variant="ghost"
                className="text-[#9AA5B5] hover:text-white hover:bg-white/5"
                data-testid="compare-cancel-btn"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {fetching ? (
          <div className="bg-[#0F1F38] border border-white/5 rounded-lg p-16 text-center text-[#9AA5B5]">
            Loading your offers…
          </div>
        ) : offers.length === 0 ? (
          <div
            className="bg-[#0F1F38] border border-white/5 rounded-lg p-16 text-center"
            data-testid="dashboard-empty-state"
          >
            <Sparkle weight="fill" className="h-10 w-10 text-[#C9A961] mx-auto mb-6" />
            <h3 className="font-display text-2xl font-light mb-3">
              The forge is cold.
            </h3>
            <p className="text-sm text-[#9AA5B5] mb-8 max-w-md mx-auto">
              You haven&apos;t forged an offer yet. Drop your current pitch
              into the form and watch it transform.
            </p>
            <Button
              onClick={() => navigate("/new")}
              className="bg-[#C9A961] text-[#0A1628] font-bold hover:bg-[#E0B64B] rounded-sm px-8 py-5"
              data-testid="dashboard-empty-cta"
            >
              Forge my first offer
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-12 gap-5">
            {offers.map((offer, i) => {
              const isSelected = selected.includes(offer.id);
              return (
                <motion.div
                  key={offer.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.04 }}
                  onClick={() => compareMode && toggleSelect(offer.id)}
                  className={`col-span-12 md:col-span-6 lg:col-span-4 bg-[#0F1F38] border rounded-lg p-7 flex flex-col transition-all ${
                    compareMode ? "cursor-pointer" : ""
                  } ${
                    isSelected
                      ? "border-[#C9A961] glow-amber"
                      : "border-white/5 hover:border-[#C9A961]/40"
                  }`}
                  data-testid={`offer-card-${offer.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <p className="text-[10px] tracking-[0.25em] uppercase text-[#C9A961] font-bold">
                      {new Date(offer.created_at).toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                      {offer.parent_id && (
                        <span className="ml-2 text-[#9AA5B5]">· refined</span>
                      )}
                    </p>
                    {compareMode && (
                      <div
                        className={`h-5 w-5 rounded-full border flex items-center justify-center text-[10px] font-bold ${
                          isSelected
                            ? "bg-[#C9A961] border-[#C9A961] text-[#0A1628]"
                            : "border-white/30 text-[#808A9B]"
                        }`}
                      >
                        {isSelected ? selected.indexOf(offer.id) + 1 : ""}
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-display font-bold text-lg text-white leading-snug mb-3 line-clamp-3">
                      {offer.output?.headline || "Untitled offer"}
                    </h3>
                    <p className="text-sm text-[#9AA5B5] line-clamp-3 leading-relaxed">
                      {offer.input?.current_offer}
                    </p>
                  </div>
                  <div className="mt-6 pt-4 border-t border-white/5 flex items-center justify-between">
                    {!compareMode ? (
                      <>
                        <Link
                          to={`/offer/${offer.id}`}
                          className="text-sm text-[#C9A961] hover:text-[#E0B64B] flex items-center gap-1 font-medium"
                          data-testid={`offer-open-${offer.id}`}
                        >
                          Open <ArrowRight className="h-4 w-4" />
                        </Link>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            handleDelete(offer.id);
                          }}
                          className="text-[#808A9B] hover:text-[#ef4444] transition-colors p-1"
                          data-testid={`offer-delete-${offer.id}`}
                          aria-label="Delete offer"
                        >
                          <Trash className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <div className="text-xs text-[#9AA5B5]">
                        {isSelected ? "Selected" : "Tap to select"}
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
